import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      title: 'Gros Œuvre & Fondations',
      description: 'Terrassement, préparation de terrain, coulage de dalles béton armé, pose de fondations profondes, création de vide sanitaire, dallage industriel et résidentiel.',
      badge: 'Gros Œuvre',
      image: 'https://public.readdy.ai/ai/img_res/f9a40dddc82f4ddf96bbfec10714fd99.jpg',
      large: true
    },
    {
      title: 'Élévation & Structures',
      description: 'Montage de murs porteurs en parpaings, briques haute performance, béton cellulaire. Réalisation de seuils de portes et fenêtres.',
      icon: 'ri-building-line',
      image: 'https://readdy.ai/api/search-image?query=Construction%20worker%20building%20brick%20wall%20with%20cement%20blocks%20and%20mortar%2C%20professional%20masonry%20work%2C%20clean%20construction%20site%20background%2C%20side%20view%20showing%20precise%20bricklaying%20technique%2C%20professional%20photography%2C%20natural%20lighting&width=500&height=300&seq=elevation001&orientation=landscape'
    },
    {
      title: 'Construction de Maisons Individuelles',
      description: 'Accompagnement de A à Z, du terrassement à la mise hors d\'eau/hors d\'air. Maçonnerie de précision pour villas modernes et maisons traditionnelles.',
      icon: 'ri-home-4-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20house%20under%20construction%20showing%20structural%20framework%20and%20masonry%20work%2C%20residential%20construction%20site%20with%20clean%20background%2C%20professional%20architectural%20photography%2C%20bright%20daylight%2C%20high%20quality%20detailed%20image&width=500&height=300&seq=house001&orientation=landscape'
    },
    {
      title: 'Maçonnerie de Pierre & Patrimoine',
      description: 'Spécialiste du mur en pierre sèche ou jointoyée, restauration de façades anciennes, murets de clôture décoratifs, taille de pierre.',
      image: 'https://readdy.ai/api/search-image?query=Beautiful%20traditional%20stone%20wall%20with%20natural%20stones%20perfectly%20fitted%20together%2C%20heritage%20masonry%20restoration%20work%2C%20rustic%20French%20countryside%20style%2C%20professional%20craftsmanship%20detail%2C%20warm%20natural%20lighting%2C%20textured%20stone%20surface%20close-up&width=500&height=500&seq=stone001&orientation=squarish',
      featured: true
    },
    {
      title: 'Ouvertures & Modifications de Structure',
      description: 'Création d\'ouvertures dans murs porteurs (baies vitrées, portes, fenêtres), pose d\'IPN, renforcement structurel.',
      icon: 'ri-door-open-line',
      image: 'https://readdy.ai/api/search-image?query=Structural%20modification%20work%20showing%20new%20window%20opening%20in%20load-bearing%20wall%20with%20steel%20beam%20installation%2C%20professional%20construction%20site%2C%20technical%20masonry%20work%2C%20clean%20background%2C%20professional%20photography&width=500&height=300&seq=opening001&orientation=landscape'
    },
    {
      title: 'Placo & Plâtrerie',
      description: 'Pose de cloisons en plaques de plâtre, isolation phonique et thermique, faux plafonds, bandes et enduits de finition, création d\'espaces sur mesure.',
      icon: 'ri-layout-grid-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20drywall%20installation%20work%20showing%20plasterboard%20mounting%20on%20metal%20frame%2C%20interior%20construction%20site%20with%20clean%20white%20gypsum%20boards%2C%20worker%20installing%20placo%20sheets%2C%20modern%20interior%20renovation%2C%20professional%20craftsmanship%2C%20bright%20natural%20lighting&width=500&height=300&seq=placo001&orientation=landscape'
    },
    {
      title: 'Rénovation & Second Œuvre',
      description: 'Application d\'enduits de façade (monocouche, gratté, taloché), pose de carrelage grand format, travaux d\'électricité générale, isolation et finitions intérieures.',
      icon: 'ri-paint-brush-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20facade%20rendering%20work%20with%20smooth%20plaster%20finish%2C%20renovation%20construction%20site%2C%20worker%20applying%20exterior%20coating%2C%20clean%20modern%20background%2C%20professional%20craftsmanship%2C%20natural%20daylight&width=500&height=300&seq=renovation001&orientation=landscape'
    }
  ];

  const cities = [
    'Valence', 'Tournon-sur-Rhône', 'Salaise-sur-Sanne', 'Saint-Rambert-d\'Albon',
    'Toulaud', 'Portes-lès-Valence', 'Pont-de-l\'Isère', 'Saint-Péray',
    'Tain-l\'Hermitage', 'Bourg-lès-Valence', 'Annonay', 'Davezieux',
    'Saint-Vallier', 'Beaumont-Monteux', 'Châteauneuf-sur-Isère'
  ];

  const values = [
    {
      icon: 'ri-medal-line',
      title: 'Expertise',
      description: '10+ ans d\'expérience, lecture de plans, connaissance parfaite des matériaux (chaux, ciment, pierre).',
      number: '10+'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Garanties',
      description: 'Assurance décennale à jour, devis gratuits et détaillés sous 48h, respect des délais de chantier.',
      highlight: 'Décennale'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Proximité',
      description: 'Un interlocuteur unique pour un suivi de chantier simplifié, intervention dans toute la région.',
      badge: 'Disponible'
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center flex-shrink-0">
            <img src="https://static.readdy.ai/image/c67d8bfa9f904100cebe9adf1f31e7a9/891fcb503f7951dec134dd7ce9f2b686.png" alt="Victor Zgherea Maçonnerie Logo" className="h-12 sm:h-16 md:h-20 lg:h-28 w-auto" />
          </div>
          <div className="hidden lg:flex items-center space-x-8">
            <a href="#services" className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-800 hover:text-orange-600' : 'text-white hover:text-orange-400'}`}>Services</a>
            <a href="#zone" className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-800 hover:text-orange-600' : 'text-white hover:text-orange-400'}`}>Zone d'intervention</a>
            <a href="#valeurs" className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-800 hover:text-orange-600' : 'text-white hover:text-orange-400'}`}>Nos Valeurs</a>
            <a href="#contact" className={`transition-colors whitespace-nowrap ${isScrolled ? 'text-gray-800 hover:text-orange-600' : 'text-white hover:text-orange-400'}`}>Contact</a>
          </div>
          <button onClick={() => setShowContactForm(true)} className="bg-orange-600 hover:bg-orange-700 text-white px-3 sm:px-6 md:px-8 py-2 sm:py-3 rounded-full font-semibold transition-all whitespace-nowrap text-xs sm:text-sm md:text-base flex-shrink-0">
            DEVIS GRATUIT
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-24 md:pt-28">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=Professional%20masonry%20construction%20site%20with%20natural%20stone%20wall%20building%20in%20French%20countryside%2C%20warm%20earthy%20tones%20with%20beige%20and%20brown%20stones%2C%20construction%20workers%20in%20background%2C%20professional%20craftsmanship%2C%20golden%20hour%20lighting%2C%20high%20quality%20architectural%20photography%20showing%20traditional%20French%20masonry%20techniques&width=1920&height=1080&seq=hero001&orientation=landscape" 
            alt="Chantier de maçonnerie professionnelle Victor Zgherea" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto w-full mb-16 sm:mb-24">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Entreprise de Maçonnerie Générale à Arras-sur-Rhône : Construction & Rénovation
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-8 sm:mb-12 font-light leading-relaxed max-w-4xl mx-auto">
            Vous recherchez un maçon qualifié pour une construction neuve ou une rénovation de caractère ? Victor Zgherea met son expertise au service de vos projets de bâtiment dans toute la région.
          </p>
          <button onClick={() => setShowContactForm(true)} className="bg-orange-600/90 hover:bg-orange-600 text-gray-900 px-6 sm:px-8 md:px-12 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold transition-all inline-flex items-center space-x-2 sm:space-x-3 whitespace-nowrap">
            <i className="ri-phone-line text-xl sm:text-2xl"></i>
            <span>Contactez-nous</span>
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-black/20 backdrop-blur-sm py-4 sm:py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <p className="text-white text-center text-xs sm:text-sm md:text-lg lg:text-xl font-semibold tracking-wide">
              10+ ANS D'EXPÉRIENCE • ASSURANCE DÉCENNALE • DEVIS SOUS 48H
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 sm:py-16 md:py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-start justify-between mb-8 sm:mb-12 md:mb-16">
            <div className="mb-4 sm:mb-6 md:mb-0">
              <p className="text-orange-600 font-semibold mb-2 flex items-center text-sm sm:text-base">
                <span className="w-2 h-2 bg-orange-600 rounded-full mr-2"></span>
                NOS EXPERTISES
              </p>
            </div>
            <div className="md:w-1/2">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-gray-900 leading-tight">
                Du mur en <span className="text-amber-700">pierre</span> à la maison clé en main : l'art de la <span className="text-amber-700">construction durable</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {/* Large Card */}
            <div className="md:row-span-2 relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group cursor-pointer">
              <div className="w-full h-full min-h-[400px] sm:min-h-[500px] md:min-h-[600px]">
                <img src={services[0].image} alt={services[0].title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute top-4 sm:top-6 left-4 sm:left-6">
                <span className="bg-orange-600/90 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap">
                  {services[0].badge}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent p-4 sm:p-6 md:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">{services[0].title}</h3>
                <p className="text-sm sm:text-base text-white/90 leading-relaxed">{services[0].description}</p>
              </div>
            </div>

            {/* Small Cards */}
            {services.slice(1, 3).map((service, index) => (
              <div key={index} className="bg-stone-100 rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all group cursor-pointer">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-600 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{service.description}</p>
              </div>
            ))}

            {/* Featured Card */}
            <div className="md:col-span-1 relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group cursor-pointer">
              <div className="w-full h-full min-h-[300px] sm:min-h-[400px]">
                <img src={services[3].image} alt={services[3].title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">{services[3].title}</h3>
                <p className="text-sm sm:text-base text-white/90 leading-relaxed">{services[3].description}</p>
              </div>
            </div>

            {/* Remaining Small Cards */}
            {services.slice(4).map((service, index) => (
              <div key={index} className="bg-stone-100 rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all group cursor-pointer">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-orange-600 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform">
                  <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Geographic Coverage Section */}
      <section id="zone" className="py-12 sm:py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left Side - Dark */}
            <div className="bg-gray-800 p-6 sm:p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 md:mb-8 leading-tight">
                Zone d'Intervention Drôme-Ardèche-Isère
              </h2>
              <p className="text-white/85 text-sm sm:text-base md:text-lg leading-relaxed mb-6 sm:mb-8 md:mb-10">
                Basé à Arras-sur-Rhône (07370), j'interviens dans les départements de l'Ardèche, de la Drôme et de l'Isère. Un service de proximité pour tous vos projets de maçonnerie générale, construction neuve et rénovation de caractère.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                {cities.map((city, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="w-1.5 h-1.5 bg-orange-600 rounded-full flex-shrink-0"></span>
                    <span className="text-white text-xs sm:text-sm whitespace-nowrap">{city}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Map */}
            <div className="relative bg-gray-200 min-h-[400px] sm:min-h-[500px]">
              <img 
                src="https://static.readdy.ai/image/c67d8bfa9f904100cebe9adf1f31e7a9/3ebf62651f268d693ad2127a40ef4884.png" 
                alt="Carte zone intervention maçonnerie Drôme Ardèche Isère" 
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section id="valeurs" className="py-12 sm:py-16 md:py-24 bg-stone-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23000000\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-4">Nos Valeurs & Engagements</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700">La confiance se construit sur des fondations solides</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 md:gap-12">
            {values.map((value, index) => (
              <div key={index} className="text-center relative">
                {value.number && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-4 sm:-translate-y-8 text-6xl sm:text-7xl md:text-9xl font-light text-gray-200 pointer-events-none">
                    {value.number}
                  </div>
                )}
                <div className="relative z-10">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 md:mb-8 shadow-lg">
                    <i className={`${value.icon} text-3xl sm:text-4xl md:text-5xl text-white`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{value.title}</h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3 sm:mb-4">{value.description}</p>
                  {value.highlight && (
                    <span className="inline-block bg-orange-600 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-semibold whitespace-nowrap text-sm sm:text-base">
                      {value.highlight}
                    </span>
                  )}
                  {value.badge && (
                    <span className="inline-block bg-orange-600 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full font-semibold whitespace-nowrap text-sm sm:text-base">
                      {value.badge}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-12 sm:py-16">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=Beautiful%20completed%20house%20renovation%20project%20in%20French%20countryside%2C%20modern%20renovated%20stone%20house%20with%20new%20windows%20and%20doors%2C%20professional%20masonry%20work%20finished%2C%20warm%20sunset%20lighting%2C%20architectural%20photography%20showing%20successful%20construction%20completion%2C%20clean%20landscaped%20surroundings%2C%20high%20quality%20detailed%20image&width=1920&height=1080&seq=cta001&orientation=landscape" 
            alt="Projet de rénovation terminé Victor Zgherea" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-orange-600/15"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 tracking-wide">
            PRÊT À DÉMARRER VOTRE PROJET ?
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white mb-8 sm:mb-12 font-light max-w-3xl mx-auto leading-relaxed">
            Obtenez un devis gratuit et détaillé sous 48h pour votre projet de construction ou rénovation
          </p>
          <button onClick={() => setShowContactForm(true)} className="bg-orange-600 hover:bg-orange-700 text-white px-8 sm:px-12 md:px-16 py-3 sm:py-4 md:py-5 rounded-full text-base sm:text-lg md:text-xl font-semibold transition-all inline-flex items-center space-x-2 sm:space-x-3 md:space-x-4 shadow-2xl whitespace-nowrap">
            <span>DEMANDER UN DEVIS GRATUIT</span>
            <i className="ri-arrow-right-up-line text-xl sm:text-2xl"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-gray-900 text-white pt-12 sm:pt-16 pb-6 sm:pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-12">
            {/* Logo & Slogan */}
            <div className="md:col-span-1">
              <img src="https://static.readdy.ai/image/c67d8bfa9f904100cebe9adf1f31e7a9/b4ad8185bdd59fbe2eb8bb8417229f90.png" alt="Victor Zgherea Logo" className="h-20 sm:h-24 md:h-28 w-auto mb-4 sm:mb-6" />
              <p className="text-base sm:text-lg font-serif leading-relaxed text-gray-300">
                L'art de la construction durable : du mur en pierre à la maison clé en main
              </p>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-orange-600 font-bold text-base sm:text-lg mb-4 sm:mb-6">Services</h4>
              <ul className="space-y-2 sm:space-y-3">
                <li><a href="#services" className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors cursor-pointer">Gros Œuvre</a></li>
                <li><a href="#services" className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors cursor-pointer">Élévation</a></li>
                <li><a href="#services" className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors cursor-pointer">Construction Maisons</a></li>
                <li><a href="#services" className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors cursor-pointer">Maçonnerie Pierre</a></li>
                <li><a href="#services" className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors cursor-pointer">Placo & Plâtrerie</a></li>
                <li><a href="#services" className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors cursor-pointer">Rénovation</a></li>
              </ul>
            </div>

            {/* Zones */}
            <div>
              <h4 className="text-orange-600 font-bold text-base sm:text-lg mb-4 sm:mb-6">Zones</h4>
              <ul className="space-y-2 sm:space-y-3">
                <li className="text-sm sm:text-base text-gray-300">Ardèche (07)</li>
                <li className="text-sm sm:text-base text-gray-300">Drôme (26)</li>
                <li className="text-sm sm:text-base text-gray-300">Isère (38)</li>
                <li className="text-sm sm:text-base text-gray-300">Arras-sur-Rhône</li>
                <li className="text-sm sm:text-base text-gray-300">Toute la région</li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-orange-600 font-bold text-base sm:text-lg mb-4 sm:mb-6">Contact</h4>
              <ul className="space-y-2 sm:space-y-3">
                <li className="text-sm sm:text-base text-gray-300">Arras-sur-Rhône</li>
                <li className="text-sm sm:text-base text-gray-300">07370, France</li>
                <li className="flex items-center space-x-2">
                  <i className="ri-phone-line text-orange-600"></i>
                  <a href="tel:+33602494897" className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors">06 02 49 48 97</a>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-mail-line text-orange-600"></i>
                  <a href="mailto:victor.zgherea@neuf.fr?subject=Demande de devis&body=Bonjour,%0D%0A%0D%0AJe souhaite obtenir un devis pour :%0D%0A%0D%0A[Décrivez votre projet ici]%0D%0A%0D%0ACordialement" className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors break-all">victor.zgherea@neuf.fr</a>
                </li>
                <li>
                  <button onClick={() => setShowContactForm(true)} className="text-sm sm:text-base text-orange-600 hover:text-orange-500 font-semibold transition-colors cursor-pointer whitespace-nowrap">
                    Demander un devis →
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 sm:pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <p className="text-gray-400 text-xs sm:text-sm mb-4 md:mb-0 text-center md:text-left">
                © 2025 Victor Zgherea - Maçonnerie & Rénovation. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowContactForm(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900">Demande de Devis Gratuit</h3>
              <button onClick={() => setShowContactForm(false)} className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-colors cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-2xl sm:text-3xl"></i>
              </button>
            </div>
            <p className="text-sm sm:text-base text-gray-700 mb-6 sm:mb-8">Remplissez ce formulaire et cliquez sur "Envoyer" pour ouvrir votre boîte mail</p>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              
              const nom = formData.get('nom') as string;
              const email = formData.get('email') as string;
              const telephone = formData.get('telephone') as string;
              const ville = formData.get('ville') as string;
              const type_projet = formData.get('type_projet') as string;
              const description = formData.get('description') as string;

              if (!nom || !email || !telephone || !ville || !type_projet || !description) {
                alert('Veuillez remplir tous les champs obligatoires');
                return;
              }

              if (description.length > 500) {
                alert('La description ne peut pas dépasser 500 caractères');
                return;
              }

              const emailBody = `Bonjour,%0D%0A%0D%0AJe souhaite obtenir un devis pour mon projet de maçonnerie.%0D%0A%0D%0A--- INFORMATIONS DE CONTACT ---%0D%0ANom : ${encodeURIComponent(nom)}%0D%0AEmail : ${encodeURIComponent(email)}%0D%0ATéléphone : ${encodeURIComponent(telephone)}%0D%0AVille : ${encodeURIComponent(ville)}%0D%0A%0D%0A--- DÉTAILS DU PROJET ---%0D%0AType de projet : ${encodeURIComponent(type_projet)}%0D%0A%0D%0ADescription :%0D%0A${encodeURIComponent(description)}%0D%0A%0D%0ACordialement,%0D%0A${encodeURIComponent(nom)}`;

              window.location.href = `mailto:victor.zgherea@neuf.fr?subject=${encodeURIComponent('Demande de devis - ' + type_projet)}&body=${emailBody}`;
              
              setTimeout(() => {
                setShowContactForm(false);
              }, 500);
            }} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm sm:text-base text-gray-700 font-semibold mb-2">Nom complet *</label>
                  <input type="text" name="nom" required className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent outline-none transition-all" placeholder="Votre nom" />
                </div>
                <div>
                  <label className="block text-sm sm:text-base text-gray-700 font-semibold mb-2">Email *</label>
                  <input type="email" name="email" required className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent outline-none transition-all" placeholder="votre@email.com" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm sm:text-base text-gray-700 font-semibold mb-2">Téléphone *</label>
                  <input type="tel" name="telephone" required className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent outline-none transition-all" placeholder="06 12 34 56 78" />
                </div>
                <div>
                  <label className="block text-sm sm:text-base text-gray-700 font-semibold mb-2">Ville *</label>
                  <input type="text" name="ville" required className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent outline-none transition-all" placeholder="Votre ville" />
                </div>
              </div>

              <div>
                <label className="block text-sm sm:text-base text-gray-700 font-semibold mb-2">Type de projet *</label>
                <select name="type_projet" required className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent outline-none transition-all cursor-pointer">
                  <option value="">Sélectionnez un type</option>
                  <option value="Gros œuvre & Fondations">Gros œuvre & Fondations</option>
                  <option value="Construction maison">Construction maison</option>
                  <option value="Maçonnerie de pierre">Maçonnerie de pierre</option>
                  <option value="Placo & Plâtrerie">Placo & Plâtrerie</option>
                  <option value="Rénovation">Rénovation</option>
                  <option value="Ouvertures & Modifications">Ouvertures & Modifications</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div>
                <label className="block text-sm sm:text-base text-gray-700 font-semibold mb-2">Description du projet *</label>
                <textarea name="description" required maxLength={500} rows={4} className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-600 focus:border-transparent outline-none transition-all resize-none" placeholder="Décrivez votre projet en quelques mots (max 500 caractères)"></textarea>
              </div>

              <button type="submit" className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 sm:py-4 rounded-lg font-semibold text-base sm:text-lg transition-all whitespace-nowrap">
                Ouvrir ma boîte mail pour envoyer
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}